/**
 * SeatOperationPage.tsx
 * 출석부 페이지 (구 좌석 운영 모니터링)
 *
 * Features:
 * - 룸별 출석 현황 조회
 * - 배치도/목록 탭 전환
 * - WebSocket 실시간 업데이트 (입출입 시 자동 새로고침)
 * - 출석 상태: 출석, 지각, 외출, 미출석
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { FloorSelector, Floor } from '../../domains/owner/components/seat-layout/FloorSelector';
import { SeatOperationView, TodayMemoInfo } from '../../domains/owner/components/seat-layout/SeatOperationView';
import GateOpenButton from '../../domains/owner/components/seat-layout/GateOpenButton';
import { SeatDetailPanel } from '../../domains/owner/components/seat-layout/SeatDetailPanel';
import { AttendanceTableView } from '../../domains/owner/components/seat-layout/AttendanceTableView';
import { AttendanceTimeBand } from '../../domains/owner/components/seat-layout/AttendanceTimeBand';
import { useSeatOperation } from '../../domains/owner/hooks/useSeatOperation';
import { useToast } from '../../contexts/ToastContextOptimized';
import { seatOperationRepository, RuntimeFloorLayout, RuntimeSeat } from '../../domains/owner/repositories/SeatOperationRepository';
import { getTodayKST } from '../../utils/dateUtils';

type ViewMode = 'map' | 'table' | 'schedule';

// 전체 보기용 레이아웃 타입
interface AllRoomsLayout {
  rooms: {
    floor: Floor;
    layout: RuntimeFloorLayout;
  }[];
  last_updated: string;
}

// WebSocket URL 생성
const getWebSocketUrl = (): string => {
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return `wss://${window.location.host}/ws/entrance-exit-logs`;
  }
  const apiUrl = process.env.REACT_APP_API_URL || '';
  if (apiUrl) {
    return apiUrl.replace('http://', 'ws://').replace('https://', 'wss://') + '/ws/entrance-exit-logs';
  }
  return 'ws://localhost:8000/ws/entrance-exit-logs';
};

export const SeatOperationPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  // ⭐ JWT에서 현재 로그인 사용자의 organization_id 추출 (멀티 가맹점 격리)
  const [organizationId] = useState<string>(() => {
    try {
      const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      if (token) {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          return payload.organization_id || '';
        }
      }
    } catch (e) {
      console.error('[SeatOperation] JWT 파싱 실패:', e);
    }
    return '';
  });
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
  /* 🔴 휴대폰에서는 **목록**으로 시작한다 (2026-09-05 대표 판단).
        좌석 배치도는 '어디에 앉았나'를 보는 것이라 매장에서 쓸모가 있고,
        휴대폰 폭에는 격자가 다 들어가지 않아 좌석 번호가 잘린다.
        밖에서 휴대폰으로 볼 때 궁금한 것은 '누가 왔나' 라서 목록이 맞다.
        원장이 배치도 탭을 누르면 그대로 볼 수 있다 — 막지 않는다. */
  const [viewMode, setViewMode] = useState<ViewMode>(
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'table' : 'map'
  );
  const [showNeverSubmitted, setShowNeverSubmitted] = useState(false);
  // 연락 메모·출석 면제 펼침 — 학생이 늘수록 태그가 쌓여 좌석 배치도를 아래로 밀어냈다.
  // 기본은 접고, 원장이 펼쳐 두면 그 상태를 브라우저에 기억한다(매번 다시 펼치지 않게).
  const [showMemos, setShowMemos] = useState(
    () => localStorage.getItem('seatop_show_memos') === '1',
  );
  const [showExceptions, setShowExceptions] = useState(
    () => localStorage.getItem('seatop_show_exceptions') === '1',
  );
  const toggleMemos = () =>
    setShowMemos(v => { localStorage.setItem('seatop_show_memos', v ? '0' : '1'); return !v; });
  const toggleExceptions = () =>
    setShowExceptions(v => { localStorage.setItem('seatop_show_exceptions', v ? '0' : '1'); return !v; });
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // 전체 보기 관련 상태
  const [floors, setFloors] = useState<Floor[]>([]);
  const [allRoomsLayout, setAllRoomsLayout] = useState<AllRoomsLayout | null>(null);
  const [allRoomsLoading, setAllRoomsLoading] = useState(false);

  // 오늘 출석 면제 학생
  const [todayExceptions, setTodayExceptions] = useState<{id: string; student_id: string; student_name: string; reason: string}[]>([]);

  // 오늘 연락 메모
  const [todayMemos, setTodayMemos] = useState<Record<string, TodayMemoInfo>>({});

  // WebSocket 실시간 연결 상태
  const [isWsConnected, setIsWsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);

  const isAllSelected = selectedFloorId === 'all';

  // 전체화면 토글
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('전체화면 전환 실패:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('전체화면 해제 실패:', err);
      });
    }
  }, []);

  // 전체화면 상태 감지
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // 배치 수정 버튼 클릭 - 통합 레이아웃으로 이동
  const handleEditLayout = () => {
    // 현재 선택된 룸 ID를 쿼리 파라미터로 전달
    const path = selectedFloorId
      ? `/studyroom/owner/integrated-layout?room=${selectedFloorId}`
      : '/studyroom/owner/integrated-layout';
    navigate(path);
  };

  const {
    layout,
    seatDetail,
    loading,
    error,
    refreshLayout,
    loadSeatDetail,
    createNote,
    unassignStudent,
    clearSeatDetail,
  } = useSeatOperation({
    floorId: isAllSelected ? null : selectedFloorId,  // 전체 보기일 때는 null
    autoRefresh: false,  // 폴링 비활성화 - 새로고침 버튼으로 수동 갱신
  });

  // 룸 목록 로드 콜백
  const handleFloorsLoaded = useCallback((loadedFloors: Floor[]) => {
    setFloors(loadedFloors);
  }, []);

  // 전체 룸 레이아웃 새로고침
  const refreshAllRooms = useCallback(async () => {
    if (floors.length === 0) return;

    setAllRoomsLoading(true);
    try {
      const roomLayouts = await Promise.all(
        floors.map(async (floor) => {
          const layout = await seatOperationRepository.getRuntimeLayout(floor.id);
          return { floor, layout };
        })
      );

      setAllRoomsLayout({
        rooms: roomLayouts,
        last_updated: new Date().toISOString(),
      });
    } catch (err) {
      console.error('[전체 보기] 레이아웃 로드 실패:', err);
      showError('전체 레이아웃을 불러오는데 실패했습니다.');
    } finally {
      setAllRoomsLoading(false);
    }
  }, [floors, showError]);

  // WebSocket onmessage에서 사용하는 콜백/상태를 ref로 관리
  // → WebSocket effect의 의존성 배열에서 제거하여 불필요한 재연결 방지
  const refreshLayoutRef = useRef(refreshLayout);
  const refreshAllRoomsRef = useRef(refreshAllRooms);
  const isAllSelectedRef = useRef(isAllSelected);
  const selectedFloorIdRef = useRef(selectedFloorId);

  useEffect(() => { refreshLayoutRef.current = refreshLayout; }, [refreshLayout]);
  useEffect(() => { refreshAllRoomsRef.current = refreshAllRooms; }, [refreshAllRooms]);
  useEffect(() => { isAllSelectedRef.current = isAllSelected; }, [isAllSelected]);
  useEffect(() => { selectedFloorIdRef.current = selectedFloorId; }, [selectedFloorId]);

  // WebSocket 연결 (입출입 로그 실시간 수신 → 자동 새로고침)
  // 의존성 빈 배열: 마운트 시 1회만 연결, ref를 통해 최신 콜백/상태 참조
  useEffect(() => {
    let isMounted = true;

    const connectWebSocket = () => {
      // 매 연결/재연결 시 최신 토큰 사용
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.warn('[출석부 WebSocket] 토큰 없음, 5초 후 재시도');
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMounted) connectWebSocket();
        }, 5000);
        return;
      }

      if (wsRef.current?.readyState === WebSocket.OPEN ||
          wsRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(token)}`;
      console.log('[출석부 WebSocket] 연결 시도');

      try {
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[출석부 WebSocket] ✅ 연결 성공');
          if (isMounted) {
            setIsWsConnected(true);
            reconnectAttemptsRef.current = 0;
          }
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            // entrance_exit_log: 출입 로그 INSERT 직후
            // student_state_changed: student_current_state UPSERT 직후 (Phase C, 더 빠름)
            if (
              message.type === 'entrance_exit_log' ||
              message.type === 'student_state_changed'
            ) {
              console.log(
                `[출석부 WebSocket] 📡 ${message.type} → 자동 새로고침`,
                message.data
              );
              // ref를 통해 최신 함수/상태 참조
              if (isAllSelectedRef.current) {
                refreshAllRoomsRef.current();
              } else if (selectedFloorIdRef.current) {
                refreshLayoutRef.current();
              }
            }
          } catch (e) {
            // 파싱 실패 무시
          }
        };

        ws.onclose = () => {
          console.log('[출석부 WebSocket] 연결 종료');
          if (!isMounted) return;
          setIsWsConnected(false);
          wsRef.current = null;

          // 무한 재연결 (최대 60초 간격, 지수 백오프)
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 60000);
          console.log(`[출석부 WebSocket] ${delay / 1000}초 후 재연결 시도 (#${reconnectAttemptsRef.current + 1})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connectWebSocket();
          }, delay);
        };

        ws.onerror = () => {
          console.error('[출석부 WebSocket] 에러');
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('[출석부 WebSocket] 연결 생성 실패:', error);
      }
    };

    connectWebSocket();

    // Ping 유지 (30초마다)
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(pingInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 전체 보기 선택 시 모든 룸 레이아웃 로드
  useEffect(() => {
    if (isAllSelected && floors.length > 0) {
      refreshAllRooms();
    }
  }, [isAllSelected, floors, refreshAllRooms]);

  // 오늘 출석 면제 학생 로드
  useEffect(() => {
    const loadTodayExceptions = async () => {
      try {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');
        if (!token) return;
        const apiUrl = process.env.REACT_APP_API_URL || '';
        const baseUrl = apiUrl || (window.location.protocol === 'https:' ? `https://${window.location.host}` : 'http://localhost:8000');
        const today = getTodayKST();
        const [year, month] = today.split('-');
        const res = await fetch(`${baseUrl}/api/v1/owner/schedule-exceptions?year=${year}&month=${month}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const todayOnly = data.filter((e: any) => (e.exception_date === today || e.exception_date?.startsWith(today)) && (e.status === 'approved' || !e.status));
          setTodayExceptions(todayOnly.map((e: any) => ({
            id: e.id,
            student_id: e.student_id || '',
            student_name: e.student_name || '알 수 없음',
            reason: e.reason,
          })));
        }
      } catch (err) {
        console.error('[출석 면제] 로드 실패:', err);
      }
    };
    loadTodayExceptions();
  }, []);

  // 오늘 연락 메모 로드
  const loadTodayMemos = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('auth_access_token');
      if (!token) return;
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const baseUrl = apiUrl || (window.location.protocol === 'https:' ? `https://${window.location.host}` : 'http://localhost:8000');
      const res = await fetch(`${baseUrl}/api/v1/owner/member-memos/today-contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setTodayMemos(data.data);
        }
      }
    } catch (err) {
      console.error('[연락 메모] 로드 실패:', err);
    }
  }, []);

  useEffect(() => {
    loadTodayMemos();
  }, [loadTodayMemos]);

  const handleFloorChange = (floorId: string) => {
    setSelectedFloorId(floorId);
    clearSeatDetail();
  };

  const handleSeatClick = async (seatId: string) => {
    try {
      await loadSeatDetail(seatId);
    } catch (err) {
      console.error('좌석 상세 정보 로드 실패:', err);
      showError('좌석 정보를 불러오는데 실패했습니다.');
    }
  };

  const handleCreateNote = async (seatId: string, note: any) => {
    try {
      await createNote(seatId, note);
      showSuccess('순찰 노트가 저장되었습니다.');
      // 노트 저장 후 상�� 정보 새로고�� (recent_notes 갱신)
      await loadSeatDetail(seatId);
    } catch (err) {
      console.error('순찰 노트 작성 실패:', err);
      showError('순찰 노트 저장에 실패했습니다.');
      throw err;
    }
  };

  const handleUnassignStudent = async (seatId: string) => {
    try {
      await unassignStudent(seatId);
      showSuccess('학생 배정이 해제되었습니다.');
    } catch (err) {
      console.error('배정 해제 실패:', err);
      showError('학생 배정 해제에 실패했습니다.');
      throw err;
    }
  };

  // 좌석 목록 (전체 보기 또는 개별 룸)
  const filteredSeats = useMemo(() => {
    if (isAllSelected && allRoomsLayout) {
      // 전체 보기: 모든 룸의 좌석을 합침
      return allRoomsLayout.rooms.flatMap(r => r.layout.seats);
    }
    return layout?.seats || [];
  }, [isAllSelected, allRoomsLayout, layout]);

  // 출석 통계 계산
  // ⭐ 스케줄없음 정의 — "오늘 입실 시간이 없는 좌석" (학생 미배정 + 학생 있지만 expected_check_in 없음)
  //   schedule_status 가 null 이어도 expected_check_in 있으면 '대기 중' 으로 간주, 스케줄없음 아님
  const attendanceStats = useMemo(() => {
    return {
      total: filteredSeats.length,
      present: filteredSeats.filter(s => s.schedule_status === 'present').length,
      arrivingSoon: filteredSeats.filter(s => s.schedule_status === 'arriving').length,
      out: filteredSeats.filter(s => s.schedule_status === 'out').length,
      absent: filteredSeats.filter(s => s.schedule_status === 'absent').length,
      exempt: filteredSeats.filter(s => s.schedule_status === 'exempt').length,
      scheduledWaiting: filteredSeats.filter(s =>
        !!s.expected_check_in &&
        s.schedule_status !== 'present' &&
        s.schedule_status !== 'arriving' &&
        s.schedule_status !== 'out' &&
        s.schedule_status !== 'absent' &&
        s.schedule_status !== 'exempt' &&
        s.schedule_status !== 'exited'
      ).length,
      notScheduled: filteredSeats.filter(s =>
        !s.expected_check_in && (s.schedule_status === 'not_scheduled' || !s.schedule_status)
      ).length,
    };
  }, [filteredSeats]);

  // 스케줄을 아예 한 번도 안 낸 학생 — 원장이 독촉해야 할 명단
  const neverSubmittedStudents = useMemo(() => {
    return filteredSeats
      .filter(s => s.student_id && s.schedule_never_submitted)
      .map(s => ({
        student_id: s.student_id as string,
        name: s.student_name || '(이름 없음)',
        seat_number: s.seat_number,
        phone: s.student_phone,
        parent_phone: s.student_parent_phone,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [filteredSeats]);

  // 마지막 업데이트 시간
  const lastUpdated = useMemo(() => {
    if (isAllSelected && allRoomsLayout) {
      return allRoomsLayout.last_updated;
    }
    return layout?.last_updated;
  }, [isAllSelected, allRoomsLayout, layout]);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header
          🔴 좁은 화면(휴대폰)에서는 한 줄에 다 넣지 않는다. 종전엔 제목·버튼을
             억지로 한 줄에 담아 '출석부'가 한 글자씩 세로로 쪼개졌다.
             flex-wrap 으로 넘치면 아래줄로 내리고, 글자는 줄바꿈을 막는다. */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-2">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">출석부</h1>
            {/* 실시간 연결 상태 — 불빛 하나로만 (2026-09-10 대표 지시)
                종전엔 '실시간 연결됨' 글자 배지라 잘 되고 있을 때도 자리를 차지했다.
                끊김 자체는 남긴다 — 끊기면 학생이 들어와도 화면이 안 바뀌는데,
                표시가 없으면 원장이 '아직 안 왔네' 하고 오해한다.
                빨간 불은 깜빡여서 눈에 띄게 한다. 대부분 몇 초 안에 저절로 돌아온다. */}
            <span
              title={
                isWsConnected
                  ? '실시간 연결됨 — 학생이 들어오면 화면이 바로 바뀝니다'
                  : '연결 끊김 — 다시 연결하는 중입니다. 잠시 화면이 자동으로 바뀌지 않습니다'
              }
              className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
                isWsConnected ? 'bg-green-500' : 'bg-red-500 animate-pulse'
              }`}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            {/* 게이트 열기 버튼 (운영자 직접 호출) */}
            <GateOpenButton />

            {/* 뷰 모드 전환 탭 — 좁은 화면에서 글자가 세로로 쪼개지지 않게 줄바꿈 금지 */}
            <div className="flex bg-gray-100 rounded-lg p-1 flex-none">
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  viewMode === 'map'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                배치도
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  viewMode === 'table'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                목록
              </button>
              <button
                onClick={() => setViewMode('schedule')}
                className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  viewMode === 'schedule'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                🕐 시간별스케줄
              </button>
            </div>

            <FloorSelector
              organizationId={organizationId}
              selectedFloorId={selectedFloorId}
              onFloorChange={handleFloorChange}
              onFloorsLoaded={handleFloorsLoaded}
              showAllOption={true}
              allowManage={false}
            />

            <button
              onClick={isAllSelected ? refreshAllRooms : refreshLayout}
              disabled={(isAllSelected ? allRoomsLoading : loading) || !selectedFloorId}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {(isAllSelected ? allRoomsLoading : loading) ? '새로고침 중...' : '새로고침'}
            </button>

            {/* 배치 수정 버튼 */}
            <button
              onClick={handleEditLayout}
              disabled={!selectedFloorId}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium transition-colors"
            >
              배치 수정
            </button>

            {/* 전체화면 버튼 */}
            <button
              onClick={toggleFullscreen}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
              title={isFullscreen ? '전체화면 해제 (ESC)' : '전체화면'}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
              <span className="hidden sm:inline">{isFullscreen ? '축소' : '전체화면'}</span>
            </button>
          </div>
        </div>

        {/* 출석 통계 */}
        {(layout || allRoomsLayout) && (
          /* 🔴 좁은 화면에서 한 줄에 다 넣으면 '전체:' 가 '전 / 체:' 로 쪼개진다.
                넘치면 아랫줄로 흐르게 하고, 항목 하나하나는 줄바꿈을 막는다. */
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 sm:gap-x-6 text-sm">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-medium text-gray-700">전체:</span>
              <span className="text-gray-900 font-semibold">{attendanceStats.total}명</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-700">출석:</span>
              <span className="text-green-600 font-semibold">{attendanceStats.present}명</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-gray-700">곧 입실:</span>
              <span className="text-orange-600 font-semibold">{attendanceStats.arrivingSoon}명</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-gray-700">외출:</span>
              <span className="text-yellow-600 font-semibold">{attendanceStats.out}명</span>
            </div>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-700">미출석:</span>
              <span className="text-red-600 font-semibold">{attendanceStats.absent}명</span>
            </div>
            {attendanceStats.scheduledWaiting > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                <span className="text-gray-700">대기:</span>
                <span className="text-blue-600 font-semibold">{attendanceStats.scheduledWaiting}명</span>
              </div>
            )}
            {attendanceStats.exempt > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-gray-700">면제:</span>
                <span className="text-purple-600 font-semibold">{attendanceStats.exempt}명</span>
              </div>
            )}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <span className="text-gray-700">스케줄없음:</span>
              <span className="text-gray-500 font-semibold">{attendanceStats.notScheduled}명</span>
            </div>
            <div className="ml-auto text-xs text-gray-500">
              마지막 업데이트: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString('ko-KR') : '-'}
            </div>
          </div>
        )}


        {/* 눌러서 명단을 보는 것끼리 한 줄에 모은다 (2026-09-10 대표 지시)
            ① 스케줄 미제출 ② 연락 메모 ③ 출석 면제 — 셋 다 '접었다 펴는' 성격인데
            ①만 통계 줄에 섞여 있고 ②③은 늘 펼쳐진 채 태그가 쌓여 좌석 배치도를
            아래로 밀어냈다. 이제 위는 '숫자 보는 줄', 아래는 '눌러서 명단 보는 줄'이다.
            펼침 상태는 브라우저에 기억한다. */}
        {(neverSubmittedStudents.length > 0 || Object.keys(todayMemos).length > 0 || todayExceptions.length > 0) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {neverSubmittedStudents.length > 0 && (
              <button
                onClick={() => setShowNeverSubmitted(v => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-800 rounded-full text-xs font-medium border border-amber-300 hover:bg-amber-100 transition-colors"
                title="스케줄을 아예 한 번도 내지 않은 학생"
              >
                🔶 스케줄 미제출 {neverSubmittedStudents.length}명
                <span className="text-amber-600">{showNeverSubmitted ? '▲' : '▼'}</span>
              </button>
            )}
            {Object.keys(todayMemos).length > 0 && (
              <button
                onClick={toggleMemos}
                className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium border border-orange-200 hover:bg-orange-100 transition-colors"
                title="오늘 연락 메모가 있는 학생"
              >
                📞 연락 메모 {Object.keys(todayMemos).length}명
                <span className="text-orange-500">{showMemos ? '▲' : '▼'}</span>
              </button>
            )}
            {todayExceptions.length > 0 && (
              <button
                onClick={toggleExceptions}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200 hover:bg-blue-100 transition-colors"
                title="오늘 출석이 면제된 학생 (사유서 승인)"
              >
                📅 오늘 출석 면제 {todayExceptions.length}명
                <span className="text-blue-500">{showExceptions ? '▲' : '▼'}</span>
              </button>
            )}
          </div>
        )}

        {/* 스케줄 미제출 학생 명단 — 독촉 전화용 */}
        {showNeverSubmitted && neverSubmittedStudents.length > 0 && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-sm font-semibold text-amber-900 mb-2">
              스케줄 미제출 {neverSubmittedStudents.length}명 — 등록 안내가 필요합니다
            </div>
            <div className="flex flex-wrap gap-2">
              {neverSubmittedStudents.map(s => (
                <div
                  key={s.student_id}
                  className="flex items-center gap-2 px-2 py-1 bg-white border border-amber-300 rounded text-sm"
                >
                  <span className="text-gray-500 text-xs">{s.seat_number}번</span>
                  <span className="font-medium text-gray-900">{s.name}</span>
                  {s.phone && (
                    <a href={`tel:${s.phone}`} className="text-blue-600 hover:underline text-xs">
                      📞학생
                    </a>
                  )}
                  {s.parent_phone && (
                    <a href={`tel:${s.parent_phone}`} className="text-blue-600 hover:underline text-xs">
                      📞학부모
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {showMemos && Object.keys(todayMemos).length > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {Object.values(todayMemos).map((memo) => {
              // 해당 학생의 이름을 좌석에서 찾기
              const seat = filteredSeats.find(s => s.student_id === memo.student_id);
              return (
                <span key={memo.student_id} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-600 rounded-full text-xs">
                  {seat?.student_name || '?'} <span className="text-orange-400">({memo.content})</span>
                </span>
              );
            })}
          </div>
        )}

        {showExceptions && todayExceptions.length > 0 && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            {todayExceptions.map((exc) => (
              <span key={exc.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 rounded-full text-xs">
                {exc.student_name} <span className="text-blue-400">({exc.reason})</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!selectedFloorId && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-lg font-medium mb-2">층을 선택해주세요</p>
              <p className="text-sm">상단의 층 선택 드롭다운에서 층을 선택하세요.</p>
            </div>
          </div>
        )}

        {selectedFloorId && !isAllSelected && !layout && !loading && (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">출석 정보를 불러오는 중...</p>
          </div>
        )}

        {isAllSelected && allRoomsLoading && (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">전체 룸 출석 정보를 불러오는 중...</p>
          </div>
        )}

        {/* 배치도 뷰 */}
        {viewMode === 'map' && (
          <>
            {/* 전체 보기: 룸별로 구분하여 표시 */}
            {isAllSelected && allRoomsLayout && (
              <div className="space-y-8">
                {allRoomsLayout.rooms.map((room, index) => (
                  <div key={room.floor.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* 룸 헤더 */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-3 border-b border-gray-200">
                      {/* 좁은 화면에서 '1층 총 20석' 이 세로로 쪼개지던 곳 */}
                      <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 whitespace-nowrap">
                          <span className="text-2xl">🏠</span>
                          {room.floor.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:gap-4 text-sm [&>span]:whitespace-nowrap">
                          <span className="text-gray-600">
                            총 <span className="font-semibold text-blue-600">{room.layout.seats.length}</span>석
                          </span>
                          <span className="text-gray-400">|</span>
                          <span className="text-green-600">
                            출석 <span className="font-semibold">{room.layout.seats.filter(s => s.schedule_status === 'present').length}</span>
                          </span>
                          <span className="text-orange-600">
                            곧 입실 <span className="font-semibold">{room.layout.seats.filter(s => s.schedule_status === 'arriving').length}</span>
                          </span>
                          <span className="text-red-600">
                            미출석 <span className="font-semibold">{room.layout.seats.filter(s => s.schedule_status === 'absent').length}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* 룸 배치도 */}
                    <div className="p-4 overflow-x-auto">
                      <SeatOperationView
                        seats={room.layout.seats}
                        canvasWidth={room.layout.canvas_width}
                        canvasHeight={room.layout.canvas_height}
                        onSeatClick={handleSeatClick}
                        todayMemos={todayMemos}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 개별 룸 보기 */}
            {!isAllSelected && layout && (
              <div className="overflow-x-auto">
                <SeatOperationView
                  seats={filteredSeats}
                  canvasWidth={layout.canvas_width}
                  canvasHeight={layout.canvas_height}
                  onSeatClick={handleSeatClick}
                  todayMemos={todayMemos}
                />
              </div>
            )}
          </>
        )}

        {/* 테이블 뷰 */}
        {viewMode === 'table' && (
          <>
            {/* 전체 보기: 룸별로 구분하여 테이블 표시 */}
            {isAllSelected && allRoomsLayout && (
              <div className="space-y-6">
                {allRoomsLayout.rooms.map((room, index) => (
                  <div key={room.floor.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    {/* 룸 헤더 */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                          <span className="text-2xl">🏠</span>
                          {room.floor.name}
                        </h3>
                        <span className="text-sm text-gray-600">
                          총 <span className="font-semibold text-blue-600">{room.layout.seats.length}</span>석
                        </span>
                      </div>
                    </div>
                    {/* 룸 테이블 */}
                    <div className="p-4 overflow-x-auto">
                      <AttendanceTableView
                        seats={room.layout.seats}
                        onStudentClick={handleSeatClick}
                        roomId={room.floor?.id || null}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 개별 룸 보기 */}
            {!isAllSelected && layout && (
              <div className="overflow-x-auto">
                <AttendanceTableView
                  seats={filteredSeats}
                  onStudentClick={handleSeatClick}
                  roomId={selectedFloorId}
                />
              </div>
            )}
          </>
        )}

        {/* 시간별 스케줄 뷰 — 시간 단일진실 연속 밴드(교시 구분선 + 교시별출결) */}
        {viewMode === 'schedule' && (
          <>
            {!selectedFloorId && (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
                룸을 선택하세요.
              </div>
            )}
            {isAllSelected && allRoomsLayout && (
              <div className="space-y-6">
                {allRoomsLayout.rooms.map((room) => (
                  <div key={room.floor.id}>
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                      <span className="text-2xl">🏠</span>
                      {room.floor.name}
                    </h3>
                    <AttendanceTimeBand
                      roomId={room.floor?.id || null}
                      onStudentClick={(studentId) => {
                        const seat = room.layout.seats.find(
                          (s: any) => s.student_id === studentId,
                        );
                        if (seat) handleSeatClick(seat.seat_id);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {!isAllSelected && selectedFloorId && (
              <AttendanceTimeBand
                roomId={selectedFloorId}
                onStudentClick={(studentId) => {
                  const seat = filteredSeats.find(
                    (s: any) => s.student_id === studentId,
                  );
                  if (seat) handleSeatClick(seat.seat_id);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* 좌석 상세 패널 */}
      {seatDetail && (
        <SeatDetailPanel
          seatDetail={seatDetail}
          onClose={clearSeatDetail}
          onCreateNote={handleCreateNote}
          onUnassignStudent={handleUnassignStudent}
          onRefresh={loadSeatDetail}
        />
      )}
    </div>
  );
};

export default SeatOperationPage;
